import React, { useState, useEffect } from 'react';
import { X, Sparkles, Clock, DollarSign, Globe, Leaf } from 'lucide-react';

function minToTimeString(mins) {
  const clamped = Math.max(0, Math.min(1439, Math.round(mins)));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function timeStringToMin(timeStr = '07:30') {
  if (!timeStr) return 450;
  const parts = String(timeStr).split(':').map(Number);
  const h = !isNaN(parts[0]) ? parts[0] : 7;
  const m = !isNaN(parts[1]) ? parts[1] : 30;
  return Math.max(0, Math.min(1439, h * 60 + m));
}

function IonicToggle({ checked, onChange, disabled = false }) {
  return (
    <label
      className="switch"
      style={{
        position: 'relative',
        display: 'inline-block',
        width: '42px',
        height: '22px',
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        flexShrink: 0,
        margin: 0
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={e => !disabled && onChange(e.target.checked)}
        style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
      />
      <span
        style={{
          position: 'absolute',
          cursor: disabled ? 'not-allowed' : 'pointer',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: checked ? '#38bdf8' : '#475569',
          transition: '0.2s',
          borderRadius: '22px'
        }}
      >
        <span
          style={{
            position: 'absolute',
            height: '16px',
            width: '16px',
            left: checked ? '22px' : '3px',
            bottom: '3px',
            backgroundColor: '#ffffff',
            transition: '0.2s',
            borderRadius: '50%',
            boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
          }}
        />
      </span>
    </label>
  );
}

export default function JobOptimizationOptionsModal({
  isOpen,
  onClose,
  job,
  stops = [],
  stopDependencies = [],
  drivers = [],
  driverShifts = [],
  rounds = [],
  currentDate = null,
  onLaunchPreview
}) {
  const [objective, setObjective] = useState('cost');
  const [flexibleStartTime, setFlexibleStartTime] = useState(false);
  const [fixedStartTime, setFixedStartTime] = useState('07:30');
  const [shiftStartMin, setShiftStartMin] = useState(450); // 07:30
  const [shiftEndMin, setShiftEndMin] = useState(1020);    // 17:00
  const [enforceTimeWindows, setEnforceTimeWindows] = useState(true);
  const [enforceWeight, setEnforceWeight] = useState(true);
  const [enforceVolume, setEnforceVolume] = useState(true);
  const [enforceDependencies, setEnforceDependencies] = useState(true);
  const [enforceMaxWorkTime, setEnforceMaxWorkTime] = useState(true);
  const [maxWorkHours, setMaxWorkHours] = useState(8);
  const [enforceForcedPauses, setEnforceForcedPauses] = useState(false);
  const [forcedPauses, setForcedPauses] = useState([
    { id: 1, duration_mins: 30, start_time: '12:00', end_time: '14:00' }
  ]);

  // Synchronize initial start/end times when opening or when job changes
  useEffect(() => {
    if (!job) return;

    const jobStartTime = (job.estimated_departure || '07:30').slice(0, 5);
    setFixedStartTime(jobStartTime);

    let shiftStart = jobStartTime;
    let shiftEnd = '17:00';

    const driverId = job.driver_id;
    const targetDate = (job.delivery_date ? String(job.delivery_date).slice(0, 10) : null) ||
                       (currentDate ? String(currentDate).slice(0, 10) : null);

    if (driverId && Array.isArray(driverShifts) && driverShifts.length > 0) {
      const match = driverShifts.find(s => {
        if (s.driver_id !== driverId) return false;
        if (!targetDate) return true;
        const sDate = String(s.start_datetime || '').slice(0, 10);
        return sDate === targetDate;
      }) || driverShifts.find(s => s.driver_id === driverId);

      if (match) {
        const sTime = String(match.start_datetime).includes('T')
          ? String(match.start_datetime).split('T')[1].slice(0, 5)
          : String(match.start_datetime).split(' ')[1]?.slice(0, 5);
        const eTime = String(match.end_datetime).includes('T')
          ? String(match.end_datetime).split('T')[1].slice(0, 5)
          : String(match.end_datetime).split(' ')[1]?.slice(0, 5);
        if (sTime) shiftStart = sTime;
        if (eTime) shiftEnd = eTime;
      }
    } else if (job.round_id && Array.isArray(rounds)) {
      const round = rounds.find(r => r.id === job.round_id);
      if (round) {
        if (round.default_start_time) shiftStart = round.default_start_time.slice(0, 5);
        if (round.default_end_time) shiftEnd = round.default_end_time.slice(0, 5);
      }
    }

    setShiftStartMin(timeStringToMin(shiftStart));
    setShiftEndMin(timeStringToMin(shiftEnd));
  }, [job, driverShifts, rounds, currentDate]);

  if (!isOpen || !job) return null;

  const handleLaunch = () => {
    // Validate pickup & delivery dependencies if enforced
    if (enforceDependencies) {
      const jobStops = (stops || []).filter(s => s.job_id === job.id);
      const jobStopIdsSet = new Set(jobStops.map(s => s.id));

      for (const s of jobStops) {
        let missingPartnerId = null;
        if (s.related_pickup_stop_id && !jobStopIdsSet.has(parseInt(s.related_pickup_stop_id))) {
          missingPartnerId = s.related_pickup_stop_id;
        } else if (s.related_delivery_stop_id && !jobStopIdsSet.has(parseInt(s.related_delivery_stop_id))) {
          missingPartnerId = s.related_delivery_stop_id;
        } else if (Array.isArray(stopDependencies)) {
          const depEdge = stopDependencies.find(d => (d.predecessor_stop_id === s.id && !jobStopIdsSet.has(d.successor_stop_id)) || (d.successor_stop_id === s.id && !jobStopIdsSet.has(d.predecessor_stop_id)));
          if (depEdge) {
            missingPartnerId = depEdge.predecessor_stop_id === s.id ? depEdge.successor_stop_id : depEdge.predecessor_stop_id;
          }
        }

        if (missingPartnerId) {
          const partnerStop = (stops || []).find(ps => ps.id === parseInt(missingPartnerId));
          const partnerName = partnerStop ? (partnerStop.stop_number || `STOP-${partnerStop.id}`) : `STOP-${missingPartnerId}`;
          alert(`Impossible d'optimiser cette mission : le stop ${s.stop_number || `STOP-${s.id}`} est lié à ${partnerName} qui n'est pas présent dans cette mission. Veuillez inclure le stop lié ou décocher le respect des dépendances.`);
          return;
        }
      }
    }

    const options = {
      objective,
      flexible_start_time: flexibleStartTime,
      start_time: flexibleStartTime ? minToTimeString(shiftStartMin) : fixedStartTime,
      end_time: flexibleStartTime ? minToTimeString(shiftEndMin) : undefined,
      enforce_time_windows: enforceTimeWindows,
      enforce_weight: enforceWeight,
      enforce_volume: enforceVolume,
      max_work_time_mins: enforceMaxWorkTime ? Math.round((parseFloat(maxWorkHours) || 8) * 60) : null,
      enforce_dependencies: enforceDependencies,
      dependency_mode: enforceDependencies ? 'strict' : 'ignore',
      enforce_skills: false,
      enforce_forced_pauses: enforceForcedPauses,
      forced_pauses: enforceForcedPauses ? forcedPauses : []
    };
    onLaunchPreview(job.id, options);
    onClose();
  };

  const shiftDurationMins = Math.max(0, shiftEndMin - shiftStartMin);
  const shiftDurH = Math.floor(shiftDurationMins / 60);
  const shiftDurM = shiftDurationMins % 60;

  return (
    <div className="modal-overlay" style={{ zIndex: 1150 }}>
      <style>{`
        .loops-dual-range-input {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          left: 0;
          width: 100%;
          height: 6px;
          margin: 0;
          padding: 0;
          pointer-events: none;
          -webkit-appearance: none;
          appearance: none;
          background: transparent;
          outline: none;
        }
        .loops-dual-range-input::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          pointer-events: auto;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #38bdf8;
          border: 2px solid #ffffff;
          cursor: grab;
          box-shadow: 0 1px 4px rgba(0,0,0,0.5);
          transition: transform 0.1s ease;
        }
        .loops-dual-range-input::-webkit-slider-thumb:active {
          cursor: grabbing;
          transform: scale(1.2);
        }
        .loops-dual-range-input::-moz-range-thumb {
          pointer-events: auto;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #38bdf8;
          border: 2px solid #ffffff;
          cursor: grab;
          box-shadow: 0 1px 4px rgba(0,0,0,0.5);
        }
        .loops-dual-range-input::-moz-range-thumb:active {
          cursor: grabbing;
          transform: scale(1.2);
        }
      `}</style>

      <div className="overlay-card" style={{ width: '540px', padding: '0', overflow: 'hidden', background: '#0f172a', border: '1px solid #334155', borderRadius: '12px' }}>
        
        {/* HEADER (Settings2 icon removed) */}
        <div style={{ background: '#1e293b', padding: '16px 20px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#f8fafc' }}>
            Options d'Optimisation : <span style={{ color: '#38bdf8' }}>{job.display_name || `Mission #${job.id}`}</span>
          </h3>

          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px' }}>
            <X size={20} />
          </button>
        </div>

        {/* BODY CONTENT */}
        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          
          {/* SECTION 1: OBJECTIVES GRID (Header & Subtitles removed, CO2 option added) */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
            
            {/* Coût Total */}
            <div
              onClick={() => setObjective('cost')}
              style={{
                background: objective === 'cost' ? 'rgba(56, 189, 248, 0.15)' : '#1e293b',
                border: objective === 'cost' ? '2px solid #38bdf8' : '1px solid #334155',
                padding: '12px 6px',
                borderRadius: '8px',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.15s ease'
              }}
            >
              <DollarSign size={18} color={objective === 'cost' ? '#38bdf8' : '#94a3b8'} style={{ margin: '0 auto 4px auto' }} />
              <div style={{ fontSize: '0.8rem', fontWeight: '700', color: objective === 'cost' ? '#38bdf8' : '#f8fafc' }}>Coût Total</div>
            </div>

            {/* Distance */}
            <div
              onClick={() => setObjective('distance')}
              style={{
                background: objective === 'distance' ? 'rgba(34, 197, 94, 0.15)' : '#1e293b',
                border: objective === 'distance' ? '2px solid #22c55e' : '1px solid #334155',
                padding: '12px 6px',
                borderRadius: '8px',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.15s ease'
              }}
            >
              <Globe size={18} color={objective === 'distance' ? '#22c55e' : '#94a3b8'} style={{ margin: '0 auto 4px auto' }} />
              <div style={{ fontSize: '0.8rem', fontWeight: '700', color: objective === 'distance' ? '#22c55e' : '#f8fafc' }}>Distance</div>
            </div>

            {/* Durée */}
            <div
              onClick={() => setObjective('duration')}
              style={{
                background: objective === 'duration' ? 'rgba(245, 158, 11, 0.15)' : '#1e293b',
                border: objective === 'duration' ? '2px solid #f59e0b' : '1px solid #334155',
                padding: '12px 6px',
                borderRadius: '8px',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.15s ease'
              }}
            >
              <Clock size={18} color={objective === 'duration' ? '#f59e0b' : '#94a3b8'} style={{ margin: '0 auto 4px auto' }} />
              <div style={{ fontSize: '0.8rem', fontWeight: '700', color: objective === 'duration' ? '#f59e0b' : '#f8fafc' }}>Durée</div>
            </div>

            {/* CO2 (Greyed out) */}
            <div
              style={{
                background: '#1e293b',
                border: '1px solid #334155',
                padding: '12px 6px',
                borderRadius: '8px',
                cursor: 'not-allowed',
                textAlign: 'center',
                opacity: 0.45
              }}
              title="Optimisation de l'empreinte carbone (bientôt disponible)"
            >
              <Leaf size={18} color="#94a3b8" style={{ margin: '0 auto 4px auto' }} />
              <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#94a3b8' }}>CO₂</div>
            </div>

          </div>

          {/* SECTION 2: FLEXIBLE START TIME & TIME CONFIGURATION */}
          <div style={{ background: '#1e293b', border: '1px solid #334155', padding: '14px', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            
            {/* Toggle Row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#f8fafc', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Clock size={16} color="#38bdf8" />
                <span>Heure de départ flexible</span>
              </div>

              <IonicToggle
                checked={flexibleStartTime}
                onChange={setFlexibleStartTime}
              />
            </div>

            {/* CASE A: Fixed Start Time View & Edit */}
            {!flexibleStartTime && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '10px', borderTop: '1px solid rgba(51, 65, 85, 0.6)' }}>
                <span style={{ fontSize: '0.8rem', color: '#cbd5e1', fontWeight: '500' }}>
                  Heure de départ fixe :
                </span>
                <input
                  type="time"
                  value={fixedStartTime}
                  onChange={e => setFixedStartTime(e.target.value)}
                  style={{
                    background: '#0f172a',
                    color: '#38bdf8',
                    fontWeight: '700',
                    border: '1px solid #334155',
                    borderRadius: '6px',
                    padding: '4px 10px',
                    fontSize: '0.85rem',
                    cursor: 'pointer'
                  }}
                />
              </div>
            )}

            {/* CASE B: Flexible Start Dual Slider (00:00 to 23:59) */}
            {flexibleStartTime && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', paddingTop: '10px', borderTop: '1px solid rgba(51, 65, 85, 0.6)' }}>
                
                {/* Time values & duration indicator */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '0.76rem', color: '#94a3b8' }}>Début :</span>
                    <input
                      type="time"
                      value={minToTimeString(shiftStartMin)}
                      onChange={e => {
                        const mins = timeStringToMin(e.target.value);
                        if (mins < shiftEndMin) setShiftStartMin(mins);
                      }}
                      style={{
                        background: '#0f172a',
                        color: '#38bdf8',
                        fontWeight: '700',
                        border: '1px solid #334155',
                        borderRadius: '4px',
                        padding: '3px 6px',
                        fontSize: '0.78rem'
                      }}
                    />
                  </div>

                  <span style={{ fontSize: '0.72rem', color: '#38bdf8', background: 'rgba(56, 189, 248, 0.12)', padding: '2px 8px', borderRadius: '12px', border: '1px solid rgba(56, 189, 248, 0.3)', fontWeight: '600' }}>
                    Shift : {shiftDurH}h{String(shiftDurM).padStart(2, '0')}
                  </span>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '0.76rem', color: '#94a3b8' }}>Fin :</span>
                    <input
                      type="time"
                      value={minToTimeString(shiftEndMin)}
                      onChange={e => {
                        const mins = timeStringToMin(e.target.value);
                        if (mins > shiftStartMin) setShiftEndMin(mins);
                      }}
                      style={{
                        background: '#0f172a',
                        color: '#38bdf8',
                        fontWeight: '700',
                        border: '1px solid #334155',
                        borderRadius: '4px',
                        padding: '3px 6px',
                        fontSize: '0.78rem'
                      }}
                    />
                  </div>
                </div>

                {/* Dual Slider Container */}
                <div style={{ position: 'relative', height: '24px', display: 'flex', alignItems: 'center' }}>
                  {/* Background Track */}
                  <div style={{ position: 'absolute', left: 0, right: 0, height: '6px', backgroundColor: '#0f172a', borderRadius: '3px', border: '1px solid #334155' }} />
                  
                  {/* Selected Range Track */}
                  <div
                    style={{
                      position: 'absolute',
                      left: `${(shiftStartMin / 1439) * 100}%`,
                      width: `${Math.max(0, ((shiftEndMin - shiftStartMin) / 1439) * 100)}%`,
                      height: '6px',
                      backgroundColor: '#38bdf8',
                      borderRadius: '3px',
                      boxShadow: '0 0 8px rgba(56, 189, 248, 0.4)'
                    }}
                  />

                  {/* Range Thumb 1: Shift Start */}
                  <input
                    type="range"
                    min="0"
                    max="1439"
                    step="15"
                    value={shiftStartMin}
                    onChange={e => {
                      const val = Math.min(parseInt(e.target.value) || 0, shiftEndMin - 15);
                      setShiftStartMin(val);
                    }}
                    className="loops-dual-range-input"
                    style={{ zIndex: shiftStartMin > 1400 ? 5 : 3 }}
                  />

                  {/* Range Thumb 2: Shift End */}
                  <input
                    type="range"
                    min="0"
                    max="1439"
                    step="15"
                    value={shiftEndMin}
                    onChange={e => {
                      const val = Math.max(parseInt(e.target.value) || 0, shiftStartMin + 15);
                      setShiftEndMin(val);
                    }}
                    className="loops-dual-range-input"
                    style={{ zIndex: 4 }}
                  />
                </div>

                {/* Extrema labels */}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', color: '#64748b', marginTop: '-4px' }}>
                  <span>00:00</span>
                  <span>06:00</span>
                  <span>12:00</span>
                  <span>18:00</span>
                  <span>23:59</span>
                </div>

              </div>
            )}

          </div>

          {/* SECTION 3: CONSTRAINTS (Heading removed, Ionic switches standardized) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            
            {/* 1. Time Windows */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#1e293b', border: '1px solid #334155', padding: '10px 14px', borderRadius: '6px' }}>
              <span style={{ fontSize: '0.82rem', color: '#f8fafc', fontWeight: '600' }}>
                🕒 Respecter les créneaux horaires clients
              </span>
              <IonicToggle
                checked={enforceTimeWindows}
                onChange={setEnforceTimeWindows}
              />
            </div>

            {/* 2. Weight Capacity */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#1e293b', border: '1px solid #334155', padding: '10px 14px', borderRadius: '6px' }}>
              <span style={{ fontSize: '0.82rem', color: '#f8fafc', fontWeight: '600' }}>
                ⚖️ Respecter la capacité Poids du véhicule (kg)
              </span>
              <IonicToggle
                checked={enforceWeight}
                onChange={setEnforceWeight}
              />
            </div>

            {/* 3. Volume Capacity */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#1e293b', border: '1px solid #334155', padding: '10px 14px', borderRadius: '6px' }}>
              <span style={{ fontSize: '0.82rem', color: '#f8fafc', fontWeight: '600' }}>
                📦 Respecter la capacité Volume du véhicule (m³)
              </span>
              <IonicToggle
                checked={enforceVolume}
                onChange={setEnforceVolume}
              />
            </div>

            {/* 4. Dependencies (Pickups / Deliveries) */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#1e293b', border: '1px solid #334155', padding: '10px 14px', borderRadius: '6px' }}>
              <span style={{ fontSize: '0.82rem', color: '#f8fafc', fontWeight: '600' }}>
                🔄 Respecter les dépendances Enlèvement ↔ Livraison
              </span>
              <IonicToggle
                checked={enforceDependencies}
                onChange={setEnforceDependencies}
              />
            </div>

            {/* 5. Max Work Time (Toggle + Free Field) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', background: '#1e293b', border: '1px solid #334155', padding: '10px 14px', borderRadius: '6px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '0.82rem', color: '#f8fafc', fontWeight: '600' }}>
                  ⏱️ Temps de travail maximal
                </span>
                <IonicToggle
                  checked={enforceMaxWorkTime}
                  onChange={setEnforceMaxWorkTime}
                />
              </div>

              {enforceMaxWorkTime && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '8px', borderTop: '1px solid rgba(51, 65, 85, 0.6)' }}>
                  <span style={{ fontSize: '0.76rem', color: '#94a3b8' }}>
                    Durée maximale autorisée
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input
                      type="number"
                      min="1"
                      max="24"
                      step="0.5"
                      value={maxWorkHours}
                      onChange={e => setMaxWorkHours(parseFloat(e.target.value) || 0)}
                      style={{
                        width: '58px',
                        background: '#0f172a',
                        color: '#38bdf8',
                        fontWeight: '700',
                        border: '1px solid #334155',
                        borderRadius: '4px',
                        padding: '3px 6px',
                        fontSize: '0.82rem',
                        textAlign: 'right'
                      }}
                    />
                    <span style={{ color: '#cbd5e1', fontSize: '0.78rem', fontWeight: '600' }}>heures</span>
                    <span style={{ color: '#64748b', fontSize: '0.72rem' }}>({Math.round((parseFloat(maxWorkHours) || 0) * 60)} min)</span>
                  </div>
                </div>
              )}
            </div>

            {/* 6. Forced Pauses */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#1e293b', border: '1px solid #334155', padding: '10px 14px', borderRadius: '6px' }}>
                <span style={{ fontSize: '0.82rem', color: '#f8fafc', fontWeight: '600' }}>
                  ☕ Pauses forcées
                </span>
                <IonicToggle
                  checked={enforceForcedPauses}
                  onChange={setEnforceForcedPauses}
                />
              </div>

              {enforceForcedPauses && (
                <div style={{ marginLeft: '12px', padding: '10px 12px', background: '#0f172a', border: '1px solid #334155', borderRadius: '6px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.74rem', fontWeight: '700', color: '#94a3b8' }}>Liste des pauses obligatoires</span>
                    <button
                      type="button"
                      onClick={() => setForcedPauses(prev => [...prev, { id: Date.now(), duration_mins: 30, start_time: '12:00', end_time: '14:00' }])}
                      style={{ background: 'none', border: 'none', color: '#38bdf8', cursor: 'pointer', fontSize: '0.74rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      + Ajouter une pause
                    </button>
                  </div>

                  {forcedPauses.map((fp, idx) => (
                    <div key={fp.id || idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#1e293b', padding: '6px 8px', borderRadius: '4px', border: '1px solid #334155', fontSize: '0.75rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span style={{ color: '#cbd5e1' }}>Durée:</span>
                        <input
                          type="number"
                          min="5"
                          max="240"
                          value={fp.duration_mins}
                          onChange={e => {
                            const val = parseInt(e.target.value) || 0;
                            setForcedPauses(prev => prev.map((item, i) => i === idx ? { ...item, duration_mins: val } : item));
                          }}
                          style={{ width: '48px', background: '#0f172a', color: '#fff', border: '1px solid #334155', borderRadius: '4px', padding: '2px 4px', fontSize: '0.74rem' }}
                        />
                        <span style={{ color: '#94a3b8' }}>min</span>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span style={{ color: '#cbd5e1' }}>Plage:</span>
                        <input
                          type="time"
                          value={fp.start_time}
                          onChange={e => {
                            const val = e.target.value;
                            setForcedPauses(prev => prev.map((item, i) => i === idx ? { ...item, start_time: val } : item));
                          }}
                          style={{ background: '#0f172a', color: '#fff', border: '1px solid #334155', borderRadius: '4px', padding: '2px 4px', fontSize: '0.74rem' }}
                        />
                        <span style={{ color: '#94a3b8' }}>à</span>
                        <input
                          type="time"
                          value={fp.end_time}
                          onChange={e => {
                            const val = e.target.value;
                            setForcedPauses(prev => prev.map((item, i) => i === idx ? { ...item, end_time: val } : item));
                          }}
                          style={{ background: '#0f172a', color: '#fff', border: '1px solid #334155', borderRadius: '4px', padding: '2px 4px', fontSize: '0.74rem' }}
                        />
                      </div>

                      <button
                        type="button"
                        onClick={() => setForcedPauses(prev => prev.filter((_, i) => i !== idx))}
                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px', marginLeft: 'auto', display: 'flex', alignItems: 'center' }}
                        title="Supprimer la pause"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>

        </div>

        {/* FOOTER ACTIONS */}
        <div style={{ background: '#1e293b', padding: '14px 20px', borderTop: '1px solid #334155', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button
            className="btn-action"
            style={{ padding: '8px 16px', fontSize: '0.82rem', background: 'transparent', color: '#94a3b8', borderColor: '#475569' }}
            onClick={onClose}
          >
            Annuler
          </button>

          <button
            className="btn-action"
            style={{ padding: '8px 20px', fontSize: '0.82rem', background: '#38bdf8', color: '#0f172a', border: 'none', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px' }}
            onClick={handleLaunch}
          >
            <Sparkles size={16} />
            <span>Calculer l'optimisation</span>
          </button>
        </div>

      </div>
    </div>
  );
}
